import {
  memo, useCallback, useRef, useState,
} from 'react';
import type { MouseEventHandler } from 'react';
import {
  Card, Tooltip, Typography, Link as MuiLink, ClickAwayListener,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import HelpIcon from '@mui/icons-material/Help';
import { ProfileTypes } from '@sda/enums';
import { Checkbox } from '@client/components/forms/Checkbox';
import { CheckboxGroup } from '@client/components/forms/CheckboxGroup';
import { useFormValues } from '@client/hooks/formik.hooks';
import { zIndex } from '@client/components/common/Map/Map/common/constants';
import { useCreateShowCallLink } from '@client/hooks/useCreateShowCallLink';
import { useIsMobile } from '@client/hooks/dom.hooks';
import { PinTypes } from '../interfaces';
import type { FilterFormValues } from '../interfaces';

const useStyles = makeStyles({ name: 'MapToggle' })((theme) => ({
  root: {
    position: 'absolute',
    top: theme.spacing(2),
    left: theme.spacing(2),
  },
  card: {
    borderRadius: theme.radii.small,
    boxShadow: theme.shadows[4],
    padding: theme.spacing(1),
    zIndex: zIndex.control,
  },
  tooltip: {
    marginTop: theme.spacing(1),
    width: 250,
  },
  label: {
    marginRight: theme.spacing(1 / 2),
  },
  icon: {
    color: theme.palette.grey[400],
    fontSize: 20,
  },
}));

export const MapToggle = memo(() => {
  const { classes, cx } = useStyles();
  const isMobile = useIsMobile();

  const anchorEl = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  const {
    latLng, googlePlaceId, compiledAddress, searchType,
  } = useFormValues<FilterFormValues>();

  const showCallLinkProps = useCreateShowCallLink({ latLng, googlePlaceId, compiledAddress });

  const handleTooltipClick: MouseEventHandler<HTMLDivElement> = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();

    setOpen(!open);
  }, [open]);

  const extraParams = isMobile ? {
    open,
  } : {};

  return (
    <Card
      className={cx(classes.root, classes.card)}
      elevation={4}
      ref={anchorEl}
    >
      <CheckboxGroup
        name="pinTypes"
        row={false}
      >
        <Checkbox
          value={PinTypes.SHOWCALLS}
          margin="dense"
        >
          Show Calls
        </Checkbox>

        <Checkbox
          value={PinTypes.PROFILES}
          margin="dense"
          useFastField={false}
        >
          <span className={classes.label}>{searchType === ProfileTypes.ACT ? 'All Artists' : 'All Hosts'}</span>

          <Tooltip
            arrow={false}
            placement="bottom-start"
            leaveDelay={100}
            onClick={handleTooltipClick}
            {...extraParams}
            PopperProps={{
              anchorEl: anchorEl.current,
            }}
            slots={{
              tooltip: Card,
            }}
            slotProps={{
              tooltip: {
                className: cx(classes.card, classes.tooltip),
              },
            }}
            title={(
              <ClickAwayListener onClickAway={() => setOpen(false)}>
                <Typography variant="caption">
                  The red dots represent
                  {' '}

                  {searchType === ProfileTypes.ACT ? 'artists' : 'hosts'}

                  {' '}
                  without an active Show Call.

                  If you want to
                  {' '}

                  {searchType === ProfileTypes.ACT ? 'reach these artists' : 'perform in their area'}
                  , you can notify them by

                  {' '}

                  <MuiLink
                    id="sda-create-show-call-map-toggle-link"
                    {...showCallLinkProps}
                  >
                    creating a Show Call.
                  </MuiLink>
                </Typography>
              </ClickAwayListener>
            )}
          >
            <HelpIcon className={classes.icon} />
          </Tooltip>
        </Checkbox>
      </CheckboxGroup>
    </Card>
  );
});
